// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  MembershipReportData,
  MembershipReportPayload,
  MembershipRulesDefinition,
  membershipReportPayloadSchema,
  membershipRulesSchema,
  membershipScopeOptionsSchema,
} from '@/membership/api/membership-types';
import { DateTime } from 'luxon';
import { z } from 'zod';

const membershipRulesResponseSchema = z.object({
  data: membershipRulesSchema,
  metadata: responseMetadataSchema,
});

const scopeOptionsResponseSchema = z.object({
  data: membershipScopeOptionsSchema,
  metadata: responseMetadataSchema,
});

function toBaseURL(projectKey: string) {
  return `/v1/project/${projectKey}/membership-rules`;
}

export interface SaveMembershipRulesInput {
  projectKey: string;
  definition: MembershipRulesDefinition;
}

export interface RunMembershipRulesInput {
  projectKey: string;
  definition: MembershipRulesDefinition;
}

/**
 * A project's membership report. A project nobody has configured answers with
 * the default definition rather than nothing.
 */
export async function getMembershipRules(projectKey: string) {
  const url = toBaseURL(projectKey);
  const response = await api.get(url);
  const result = await parseResponse(
    membershipRulesResponseSchema,
    response.data,
    {
      schemaName: 'MembershipRules',
      endpoint: url,
    }
  );
  return result.data;
}

export async function getMembershipScopeOptions(projectKey: string) {
  const url = `${toBaseURL(projectKey)}/scope-options`;
  const response = await api.get(url);
  const result = await parseResponse(
    scopeOptionsResponseSchema,
    response.data,
    {
      schemaName: 'MembershipScopeOptions',
      endpoint: url,
    }
  );
  return result.data;
}

export async function saveMembershipRules(input: SaveMembershipRulesInput) {
  const url = toBaseURL(input.projectKey);
  const response = await api.put(url, { definition: input.definition });
  const result = await parseResponse(
    membershipRulesResponseSchema,
    response.data,
    {
      schemaName: 'SaveMembershipRules',
      endpoint: url,
    }
  );
  return result.data;
}

/**
 * Answers a definition over the project's window. The definition is sent in the
 * body rather than read from storage, so the dialog can run a draft. Nothing is
 * recorded.
 */
export async function runMembershipRules(
  input: RunMembershipRulesInput
): Promise<MembershipReportData> {
  const url = `${toBaseURL(input.projectKey)}/run`;
  const response = await api.post(url, { definition: input.definition });
  const result = await parseResponse(
    membershipReportPayloadSchema,
    response.data,
    {
      schemaName: 'MembershipReport',
      endpoint: url,
    }
  );
  return toDecodedReport(result.data);
}

// Assets unpacked before the loop yields. A project with thousands of them
// would hold the main thread for the whole decode otherwise, and a batch this
// size is well inside a frame.
const ASSETS_PER_BATCH = 500;

/**
 * Unpacks the base64 day masks into one buffer the charts can read.
 *
 * Every mask lands in a single buffer at a fixed width rather than an array
 * each, so a project with thousands of assets costs one allocation. The loop
 * yields between batches, which gives the browser its frames back during a long
 * decode.
 */
async function toDecodedReport(
  report: MembershipReportPayload
): Promise<MembershipReportData> {
  const bytesPerMask = Math.ceil(report.dayCount / 8);
  const masks = new Uint8Array(report.assets.length * bytesPerMask);

  for (
    let batchStart = 0;
    batchStart < report.assets.length;
    batchStart += ASSETS_PER_BATCH
  ) {
    const batchEnd = Math.min(
      batchStart + ASSETS_PER_BATCH,
      report.assets.length
    );
    for (let assetIndex = batchStart; assetIndex < batchEnd; assetIndex += 1) {
      const packedMask = atob(report.assets[assetIndex].residency);
      const maskStart = assetIndex * bytesPerMask;
      // A short mask is a malformed one. The rest stays zero, which reads as
      // absent rather than as a decode that shifted every day along.
      for (
        let byteIndex = 0;
        byteIndex < packedMask.length && byteIndex < bytesPerMask;
        byteIndex += 1
      ) {
        masks[maskStart + byteIndex] = packedMask.charCodeAt(byteIndex);
      }
    }
    if (batchEnd < report.assets.length) await toNextTask();
  }

  return {
    evaluatedAt: report.evaluatedAt,
    startOn: DateTime.fromISO(report.startOn),
    dayCount: report.dayCount,
    skus: report.skus,
    assets: report.assets.map((asset) => ({
      key: asset.key,
      serialNumber: asset.serialNumber,
      itemSku: asset.itemSku,
      locations: asset.locations,
    })),
    residency: masks,
    bytesPerMask,
  };
}

/** Hands the browser back a turn of the event loop. */
function toNextTask(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}
